import update from 'immutability-helper';
import ActionTypes from 'constants/ActionTypes';
import { KeywordGapPayload } from '../../sources/KeywordGapResultSource';
import ResultDataService from '../../services/ResultDataService';

// Type definitions for keyword data
interface KeywordGapKeyword {
    _id: string;
    kw: string;
    lid: number;
    ts: number;
    svs: number;
    msv: number[][];
    ppc: number | null;
    sv: number;
    cpc: number;
    seo: any | null;
    seo_ts: number | null;
    position: number;
    et: number;
    location: {
        _id: number;
        code: string;
        label: string;
    };
    rank?: number;
    rankUpdatedAt?: number;
    ev?: number;
}

interface KeywordGapResult {
    keywords: KeywordGapKeyword[];
    totalCount: number;
    originalItemsCount: number;
    domain: string;
}

// Type definitions for the reducer state
interface KeywordGapResultsError {
    status: number | null;
    text: string | null;
}

interface KeywordGapResultsCurrentDataParams {
    domain: string | null;
    locationId: string | number | null;
    languageId: string | number | null;
    query: string | null;
    competitors: string[] | null;
    page: number | null;
}

interface KeywordGapResultsState {
    data: {
        [key: string]: KeywordGapResult;
    };
    error: KeywordGapResultsError;
    fetching: boolean;
    fetchingLoadMore: boolean;
    isAllowedPagination: boolean;
    currentDataParams: KeywordGapResultsCurrentDataParams;
    sorting: {
        column: string | null;
        type: string;
    };
}

// Action payload types
interface KeywordGapResultsPayload {
    isAllowedPagination: boolean;
    resultsSize: number;
    results: KeywordGapResult[];
    params: {
        domain: string;
        competitors: string[];
        locationId: string | number;
        page: number;
    };
}

interface KeywordGapResultsErrorPayload {
    status: number;
    text: string;
}

type KeywordGapResultsSortingPayload = {
    column: string | null;
    type: string;
};

interface KeywordRankReceivedPayload {
    id: string;
    rank: number;
    rankUpdatedAt: number;
    contentTypes: any;
}

interface KeywordGapResultsAction {
    type: string;
    payload?: KeywordGapResultsPayload | KeywordGapResultsErrorPayload | KeywordGapResultsSortingPayload | KeywordRankReceivedPayload;
}

const initialState: KeywordGapResultsState = {
    data: {},
    error: {
        status: null,
        text: null,
    },
    fetching: true,
    fetchingLoadMore: false,
    isAllowedPagination: false,
    currentDataParams: {
        domain: null,
        locationId: null,
        languageId: null,
        query: null,
        competitors: null,
        page: null,
    },
    sorting: {
        column: 'competitorRank',
        type: 'ASC',
    },
};

const keywordGapResultsReducer = (
    state: KeywordGapResultsState = initialState,
    action: KeywordGapResultsAction,
): KeywordGapResultsState => {
    switch (action.type) {
        case ActionTypes.DATA_KEYWORD_GAP_RESULTS_FETCHING: {
            return update(state, {
                fetching: {
                    $set: true,
                },
            });
        }
        case ActionTypes.DATA_KEYWORD_GAP_RESULTS_RECEIVED: {
            const payload = action.payload as KeywordGapPayload;
            // Convert results array to object with domain as key (data is already parsed in source)
            const resultsObject: { [key: string]: KeywordGapResult } = {};
            payload.results.forEach((result: KeywordGapResult) => {
                resultsObject[result.domain] = {
                    ...result,
                    keywords: ResultDataService.calculateSearchVolumeTimeframesForCompetitor(result.keywords),
                };
            });

            return update(state, {
                data: {
                    $set: resultsObject,
                },
                fetching: {
                    $set: false,
                },
                error: {
                    $set: initialState.error,
                },
                isAllowedPagination: {
                    $set: payload.isAllowedPagination,
                },
                currentDataParams: {
                    domain: { $set: payload.params.domain },
                    locationId: { $set: payload.params.locationId },
                    languageId: { $set: null }, // API doesn't provide languageId, set to null to avoid using incorrect data
                    query: { $set: payload.params.domain },
                    competitors: { $set: payload.params.competitors },
                    page: { $set: payload.params.page },
                },
            });
        }
        case ActionTypes.DATA_KEYWORD_GAP_RESULTS_SKIPPED: {
            return update(state, {
                fetching: { $set: false },
                error: { $set: initialState.error },
            });
        }
        case ActionTypes.DATA_KEYWORD_GAP_RESULTS_EMPTY: {
            return update(state, {
                fetching: { $set: false },
                error: { $set: initialState.error },
            });
        }
        case ActionTypes.DATA_KEYWORD_GAP_RESULTS_ERROR: {
            const payload = action.payload as KeywordGapResultsErrorPayload;
            return update(state, {
                data: { $set: initialState.data },
                fetching: { $set: initialState.fetching },
                error: {
                    status: { $set: payload.status },
                    text: { $set: payload.text },
                },
            });
        }
        case ActionTypes.DATA_LOAD_MORE_KEYWORD_GAP_RESULTS_FETCHING: {
            return update(state, {
                fetchingLoadMore: {
                    $set: true,
                },
            });
        }
        case ActionTypes.DATA_LOAD_MORE_KEYWORD_GAP_RESULTS_RECEIVED: {
            const payload = action.payload as KeywordGapPayload;
            // Mirror RECEIVED branch's calculateSearchVolumeTimeframesForCompetitor so merged rows stay consistent.
            const newResultsObject: { [key: string]: KeywordGapResult } = {};
            payload.results.forEach((result: KeywordGapResult) => {
                newResultsObject[result.domain] = {
                    ...result,
                    keywords: ResultDataService.calculateSearchVolumeTimeframesForCompetitor(result.keywords),
                };
            });

            return update(state, {
                data: {
                    $merge: newResultsObject,
                },
                fetching: {
                    $set: false,
                },
                fetchingLoadMore: {
                    $set: false,
                },
                error: {
                    $set: initialState.error,
                },
                currentDataParams: {
                    domain: { $set: payload.params.domain },
                    locationId: { $set: payload.params.locationId },
                    languageId: { $set: null }, // API doesn't provide languageId, set to null to avoid using incorrect data
                    query: { $set: payload.params.domain },
                    competitors: { $set: payload.params.competitors },
                    page: { $set: payload.params.page },
                },
            });
        }
        case ActionTypes.DATA_LOAD_MORE_KEYWORD_GAP_RESULTS_SKIPPED: {
            return update(state, {
                fetchingLoadMore: {
                    $set: false,
                },
            });
        }
        case ActionTypes.DATA_LOAD_MORE_KEYWORD_GAP_RESULTS_EMPTY: {
            return update(state, {
                fetchingLoadMore: { $set: false },
                error: { $set: initialState.error },
            });
        }
        case ActionTypes.DATA_LOAD_MORE_KEYWORD_GAP_RESULTS_ERROR: {
            const payload = action.payload as KeywordGapResultsErrorPayload;
            return update(state, {
                fetchingLoadMore: { $set: initialState.fetchingLoadMore },
                error: {
                    status: { $set: payload.status },
                    text: { $set: payload.text },
                },
            });
        }
        case ActionTypes.DATA_KEYWORD_GAP_RESULTS_KEYWORD_RANK_RECEIVED: {
            const payload = action.payload as KeywordRankReceivedPayload;
            if (!payload) return state;

            // Find which domain contains the keyword and its index
            let targetDomain: string | null = null;
            let keywordIndex = -1;

            for (const domain of Object.keys(state.data)) {
                const result = state.data[domain];
                const index = result.keywords.findIndex(kw => kw._id === payload.id);
                if (index !== -1) {
                    targetDomain = domain;
                    keywordIndex = index;
                    break;
                }
            }

            // If keyword not found, return unchanged state
            if (!targetDomain || keywordIndex === -1) {
                return state;
            }

            // Update the specific keyword using nested immutability-helper syntax
            return update(state, {
                data: {
                    [targetDomain]: {
                        keywords: {
                            [keywordIndex]: {
                                rank: { $set: payload.rank },
                                rankUpdatedAt: { $set: payload.rankUpdatedAt },
                            }
                        }
                    }
                }
            });
        }
        case ActionTypes.SET_KEYWORD_GAP_RESULTS_SORTING: {
            const sortingPayload = action.payload as KeywordGapResultsSortingPayload;
            return update(state, {
                sorting: {
                    $set: sortingPayload,
                },
            });
        }
        default: {
            return state;
        }
    }
};

export default keywordGapResultsReducer;

// Export types for use in other files
export type {
    KeywordGapResultsState,
    KeywordGapResultsError,
    KeywordGapResultsCurrentDataParams,
    KeywordGapResultsPayload,
    KeywordGapResultsErrorPayload,
    KeywordGapResultsAction,
};
